import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Visibility,
  Delete,
  Undo,
  AttachMoney,
  FilterList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConsignmentForm from '../components/ConsignmentForm';
import ConsignmentStatusChip from '../components/ConsignmentStatusChip';

const Consignments = () => {
  const navigate = useNavigate();
  const [consignments, setConsignments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [payoutStatusFilter, setPayoutStatusFilter] = useState('');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [consignmentToDelete, setConsignmentToDelete] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchConsignments();
    fetchVendors();
  }, [page, rowsPerPage, vendorFilter, statusFilter, payoutStatusFilter]);

  const fetchConsignments = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      if (vendorFilter) params.vendor_id = vendorFilter;
      if (statusFilter) params.status = statusFilter;
      if (payoutStatusFilter) params.payout_status = payoutStatusFilter;

      const response = await api.get('/api/consignments', { params });
      setConsignments(response.data.consignments || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching consignments:', error);
      showSnackbar('Error loading consignments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/api/admin/clients');
      setVendors(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleCreate = () => {
    setSelectedConsignment(null);
    setFormOpen(true);
  };

  const handleEdit = (consignment) => {
    setSelectedConsignment(consignment);
    setFormOpen(true);
  };

  const handleView = (consignment) => {
    navigate(`/consignments/${consignment.id}`);
  };

  const handleFormSuccess = () => {
    setFormOpen(false);
    fetchConsignments();
    showSnackbar('Consignment saved successfully', 'success');
  };

  const handleDeleteClick = (consignment) => {
    setConsignmentToDelete(consignment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/api/consignments/${consignmentToDelete.id}`);
      showSnackbar('Consignment deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setConsignmentToDelete(null);
      fetchConsignments();
    } catch (error) {
      console.error('Error deleting consignment:', error);
      showSnackbar('Error deleting consignment', 'error');
    }
  };

  const handleReturn = async (consignment) => {
    try {
      await api.put(`/api/consignments/${consignment.id}/return`, {
        return_date: new Date().toISOString().split('T')[0],
        return_reason: 'Returned by vendor',
      });
      showSnackbar('Item marked as returned', 'success');
      fetchConsignments();
    } catch (error) {
      console.error('Error marking as returned:', error);
      showSnackbar('Error marking item as returned', 'error');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const filteredConsignments = consignments.filter((consignment) => {
    const matchesSearch =
      !searchQuery ||
      consignment.item?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      consignment.vendor?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Consignments</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/vendor-payouts')}
            startIcon={<AttachMoney />}
          >
            View Payouts
          </Button>
          <Button variant="contained" onClick={handleCreate} startIcon={<Add />}>
            New Consignment
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search items or vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                label="Vendor"
              >
                <MenuItem value="">All Vendors</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.user?.first_name} {vendor.user?.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sold">Sold</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Payout Status</InputLabel>
              <Select
                value={payoutStatusFilter}
                onChange={(e) => setPayoutStatusFilter(e.target.value)}
                label="Payout Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="calculated">Calculated</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Consignment Date</TableCell>
                <TableCell>Agreed Price</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payout Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredConsignments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No consignments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredConsignments.map((consignment) => (
                  <TableRow key={consignment.id} hover>
                    <TableCell>{consignment.item?.name || 'Unknown Item'}</TableCell>
                    <TableCell>{consignment.vendor?.name || 'Unknown Vendor'}</TableCell>
                    <TableCell>
                      {consignment.consignment_date
                        ? new Date(consignment.consignment_date).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>${parseFloat(consignment.agreed_price || 0).toFixed(2)}</TableCell>
                    <TableCell>{consignment.commission_rate}%</TableCell>
                    <TableCell>
                      <ConsignmentStatusChip status={consignment.status} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={consignment.payout_status}
                        size="small"
                        color={
                          consignment.payout_status === 'paid'
                            ? 'success'
                            : consignment.payout_status === 'calculated'
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(consignment)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(consignment)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {consignment.status === 'pending' && (
                        <Tooltip title="Mark as Returned">
                          <IconButton size="small" onClick={() => handleReturn(consignment)}>
                            <Undo />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(consignment)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Form Dialog */}
      {formOpen && (
        <ConsignmentForm
          open={formOpen}
          consignment={selectedConsignment}
          onClose={() => setFormOpen(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this consignment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Consignments;
